import React, { useEffect, useState } from 'react';
import { customerAPI } from '../api/client';
import Layout from '../components/Layout';
import RecordPreviewModal from '../components/RecordPreviewModal';

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewCustomer, setPreviewCustomer] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', postal_code: '', country: '', tax_id: '', payment_terms: '' });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.data || response.data || []);
    } catch (err) {
      console.error('Failed to load customers', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await customerAPI.update(editingId, formData);
      } else {
        await customerAPI.create(formData);
      }
      setFormData({ name: '', email: '', phone: '', address: '', city: '', state: '', postal_code: '', country: '', tax_id: '', payment_terms: '' });
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      loadCustomers();
    } catch (err) {
      console.error('Failed to save customer', err);
    }
  };

  const handleEdit = (customer) => {
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      city: customer.city,
      state: customer.state,
      postal_code: customer.postal_code,
      country: customer.country,
      tax_id: customer.tax_id,
      payment_terms: customer.payment_terms,
    });
    setEditingId(customer.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({ name: '', email: '', phone: '', address: '', city: '', state: '', postal_code: '', country: '', tax_id: '', payment_terms: '' });
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await customerAPI.delete(id);
        loadCustomers();
      } catch (err) {
        console.error('Failed to delete customer', err);
      }
    }
  };

  return (
    <Layout pageTitle="Customers" pageSubtitle="Manage your customer information and contacts">
      <div className="flex justify-end mb-8">
        <button 
          onClick={() => showForm ? handleCancel() : setShowForm(true)} 
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/20"
        >
          {showForm ? 'Cancel' : '+ New Customer'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-6">{isEditing ? 'Edit Customer' : 'Add New Customer'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID</label>
                <input
                  type="text"
                  value={formData.tax_id}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                <input
                  type="text"
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  placeholder="e.g., Net 30, Due on receipt"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                />
              </div>
              <button type="submit" className="md:col-span-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/20">
                {isEditing ? 'Update Customer' : 'Create Customer'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Phone</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">City</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tax ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Payment Terms</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => setPreviewCustomer(customer)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{customer.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{customer.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{customer.phone}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{customer.city}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{customer.tax_id}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{customer.payment_terms || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(customer);
                        }} 
                        className="rounded-lg px-2.5 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 font-medium text-sm transition"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(customer.id);
                        }} 
                        className="rounded-lg px-2.5 py-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50 font-medium text-sm transition"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {previewCustomer && (
        <RecordPreviewModal
          title={previewCustomer.name || `Customer #${previewCustomer.id}`}
          subtitle="Customer Record"
          onClose={() => setPreviewCustomer(null)}
          fields={[
            { label: 'Email', value: previewCustomer.email },
            { label: 'Phone', value: previewCustomer.phone },
            { label: 'Address', value: previewCustomer.address },
            { label: 'City', value: previewCustomer.city },
            { label: 'State', value: previewCustomer.state },
            { label: 'Postal Code', value: previewCustomer.postal_code },
            { label: 'Country', value: previewCustomer.country },
            { label: 'Tax ID', value: previewCustomer.tax_id },
            { label: 'Payment Terms', value: previewCustomer.payment_terms },
          ]}
        />
      )}
    </Layout>
  );
}

export default Customers;
